/**
 * PATH: packages/email-hub/src/feature/reader-header/triage/useDecisionTransports.ts
 * FILE_ID: EMAILHUB-READERHEADER-TRIAGE-TRANSPORTS-001
 * PURPOSE: Map every decision kind to its PROVEN transport and run it through
 *   the Phase 2 runtime with symmetric same-frame optimistic acknowledgment.
 * ROLE IN ARCHITECTURE: The one adapter between ReaderDecision and mutations.
 * MAIN EXPORTS: useDecisionTransports, decisionHasConfirmCircle.
 * NON-RESPONSIBILITIES: No layout; circles/menus/queue render elsewhere.
 * NOTES FOR FUTURE AI: Transports are the legacy endpoints VERBATIM (bill =
 *   markEmailAsBill + setCategory('general') fallback; placement = kanban-host
 *   confirm/reject APIs; org suggestion = decideOrgSuggestion). Never fork a
 *   parallel API call here — reuse the proven function. The no-invoice-
 *   attachment bill accept must not promise a drawer (legacy contract).
 */
import { useCallback } from 'react'
import type { getSamKanbanApi } from '@quotech/kanban-host'

import { markEmailAsBill } from '../../../_stubs/bills/hooks/useBills'
import {
  useEmailActions,
  useEmailJobLink,
  type Email,
} from '../../hooks/useEmails'
import { decideOrgSuggestion } from '../../referrals'
import { getReaderAction } from '../actions/registry'
import type { ReaderDecision } from '../model/types'
import type {
  ReaderActionRunner,
  ReaderRunSpec,
} from '../runtime/useReaderActionRunner'

export type DecisionVerdict = 'accept' | 'reject'

/**
 * Kinds whose PRIMARY affordance is a picker/flow, not a confirm circle:
 * estimator renders the EstimatorAllocator picker; organisation renders the
 * Move-organisation control. Both keep the ghost reject circle.
 */
export function decisionHasConfirmCircle(decision: ReaderDecision): boolean {
  return decision.kind !== 'estimator' && decision.kind !== 'organisation'
}

export interface DecisionTransportDeps {
  email: Email
  api: ReturnType<typeof getSamKanbanApi>
  runner?: ReaderActionRunner
  onPatchEmail?: (emailId: string, patch: Partial<Email>) => void
  /** Optimistic queue advance: hide this decision id locally NOW. */
  markResolved: (decisionId: string) => void
  /** Exact rollback of markResolved (server failure). */
  unmarkResolved: (decisionId: string) => void
  /** Bill accept opens the source invoice — ONLY when an attachment exists. */
  onOpenBill?: () => void
}

export interface DecisionTransports {
  /** Run one decision to a verdict through the shared runtime. */
  resolve: (decision: ReaderDecision, verdict: DecisionVerdict) => void
}

/**
 * Build the per-kind accept/reject runners.
 *
 * Inputs: the live email, kanban API, shared runner, and local queue-advance
 * callbacks. Output: one `resolve(decision, verdict)` entry point. Side
 * effects: mutations via proven transports; optimistic advance + rollback.
 * Failure modes: the runner classifies errors, rolls back the local advance,
 * and owns retry/toast copy — nothing here toasts raw errors.
 */
export function useDecisionTransports({
  email,
  api,
  runner,
  onPatchEmail,
  markResolved,
  unmarkResolved,
  onOpenBill,
}: DecisionTransportDeps): DecisionTransports {
  const { setCategory } = useEmailActions()
  const { linkToJob, rejectSuggestion } = useEmailJobLink()

  const specFor = useCallback(
    (
      decision: ReaderDecision,
      verdict: DecisionVerdict,
    ): ReaderRunSpec | null => {
      const action = getReaderAction(
        verdict === 'accept' ? 'accept_decision' : 'reject_decision',
      )
      // Same-frame symmetric ack for BOTH verdicts: the decision leaves the
      // line now; failure re-inserts it exactly (plan D269-17).
      const advance = {
        apply: () => markResolved(decision.id),
        rollback: () => unmarkResolved(decision.id),
      }
      const priorCategory = email.category ?? 'general'

      switch (decision.kind) {
        case 'bill': {
          if (verdict === 'accept') {
            return {
              action,
              optimistic: advance,
              execute: async () => {
                const result = await markEmailAsBill(email.id)
                // Drawer promise only when a source document can exist.
                if (email.has_attachments) onOpenBill?.()
                return result
              },
              successToast: email.has_attachments
                ? 'Allocated to Bills — opening bill…'
                : 'Allocated to Bills',
            }
          }
          // Legacy SmartActionBar "not a bill": re-classify to the neutral
          // locked `general` bucket so the AI cannot undo the override.
          return {
            action,
            optimistic: {
              apply: () => {
                markResolved(decision.id)
                onPatchEmail?.(email.id, { category: 'general' })
              },
              rollback: () => {
                unmarkResolved(decision.id)
                onPatchEmail?.(email.id, { category: priorCategory })
              },
            },
            execute: () => setCategory(email.id, 'general'),
            undo: {
              execute: () => setCategory(email.id, priorCategory),
              onRestored: () => {
                unmarkResolved(decision.id)
                onPatchEmail?.(email.id, { category: priorCategory })
              },
            },
            successToast: 'Marked “not a bill”',
          }
        }

        case 'estimator': {
          if (verdict === 'accept') return null // Picker-primary; no circle.
          return {
            action,
            optimistic: {
              apply: () => {
                markResolved(decision.id)
                onPatchEmail?.(email.id, { category: 'general' })
              },
              rollback: () => {
                unmarkResolved(decision.id)
                onPatchEmail?.(email.id, { category: priorCategory })
              },
            },
            execute: () => setCategory(email.id, 'general'),
            undo: {
              execute: () => setCategory(email.id, priorCategory),
              onRestored: () => {
                unmarkResolved(decision.id)
                onPatchEmail?.(email.id, { category: priorCategory })
              },
            },
            successToast: 'Reclassified to General',
          }
        }

        case 'placement': {
          const placement = decision.placement
          if (!placement) return null
          if (verdict === 'accept') {
            return {
              action,
              optimistic: advance,
              execute: () => api.confirmAiAllocation(placement.allocation_id),
              successToast: 'Filing confirmed — visibility propagated',
            }
          }
          return {
            action,
            optimistic: advance,
            execute: () =>
              api.rejectAiAllocation(placement.allocation_id, {
                reason: 'Operator rejected from reader decision line',
              }),
            successToast: 'Suggested filing removed',
          }
        }

        case 'suggested_org': {
          const suggested = email.suggested_org
          if (!suggested) return null
          return {
            action,
            optimistic: {
              apply: () => {
                markResolved(decision.id)
                onPatchEmail?.(email.id, { suggested_org: null })
              },
              rollback: () => {
                unmarkResolved(decision.id)
                onPatchEmail?.(email.id, { suggested_org: suggested })
              },
            },
            execute: () =>
              decideOrgSuggestion({
                emailId: email.id,
                targetOrgId: suggested.org_id,
                decision: verdict === 'accept' ? 'confirm' : 'dismiss',
              }),
            successToast:
              verdict === 'accept'
                ? `Granted to ${suggested.org_name}`
                : 'Suggestion dismissed',
          }
        }

        case 'suggested_job': {
          const suggested = email.suggested_job
          if (!suggested) return null
          if (verdict === 'accept') {
            return {
              action,
              optimistic: {
                apply: () => {
                  markResolved(decision.id)
                  onPatchEmail?.(email.id, {
                    job_id: suggested.job_id,
                    job_name: suggested.job_label,
                    suggested_job: null,
                  })
                },
                rollback: () => {
                  unmarkResolved(decision.id)
                  onPatchEmail?.(email.id, {
                    job_id: email.job_id ?? null,
                    job_name: email.job_name,
                    suggested_job: suggested,
                  })
                },
              },
              execute: async () => {
                const result = await linkToJob(email.id, suggested.job_id)
                if (!result) throw new Error('link_failed')
                return result
              },
              successToast: `Linked to ${suggested.job_label}`,
            }
          }
          return {
            action,
            optimistic: {
              apply: () => {
                markResolved(decision.id)
                onPatchEmail?.(email.id, { suggested_job: null })
              },
              rollback: () => {
                unmarkResolved(decision.id)
                onPatchEmail?.(email.id, { suggested_job: suggested })
              },
            },
            execute: async () => {
              const result = await rejectSuggestion(email.id, suggested.job_id)
              if (!result) throw new Error('reject_failed')
              return result
            },
            successToast: `Dismissed suggestion (${suggested.job_label})`,
          }
        }

        case 'classification': {
          if (verdict === 'accept') {
            // setCategory writes the locked EmailCategoryOverride sidecar —
            // re-pinning the CURRENT category is the "lock it in" transport.
            return {
              action,
              optimistic: {
                apply: () => {
                  markResolved(decision.id)
                  onPatchEmail?.(email.id, { category_locked: true })
                },
                rollback: () => {
                  unmarkResolved(decision.id)
                  onPatchEmail?.(email.id, { category_locked: false })
                },
              },
              execute: () => setCategory(email.id, priorCategory),
              successToast: 'Classification locked in',
            }
          }
          return {
            action,
            optimistic: {
              apply: () => {
                markResolved(decision.id)
                onPatchEmail?.(email.id, { category: 'general' })
              },
              rollback: () => {
                unmarkResolved(decision.id)
                onPatchEmail?.(email.id, { category: priorCategory })
              },
            },
            execute: () => setCategory(email.id, 'general'),
            undo: {
              execute: () => setCategory(email.id, priorCategory),
              onRestored: () => {
                unmarkResolved(decision.id)
                onPatchEmail?.(email.id, { category: priorCategory })
              },
            },
            successToast: 'Reclassified to General',
          }
        }

        case 'organisation':
          // Accept opens the Move-organisation flow (primary affordance);
          // reject is a LOCAL acknowledge — the blocker has no server verb.
          return null
      }
    },
    [
      api,
      email,
      linkToJob,
      markResolved,
      onOpenBill,
      onPatchEmail,
      rejectSuggestion,
      setCategory,
      unmarkResolved,
    ],
  )

  const resolve = useCallback(
    (decision: ReaderDecision, verdict: DecisionVerdict) => {
      if (!runner) return
      if (decision.kind === 'organisation' && verdict === 'reject') {
        // Local acknowledge: stay in the home organisation and advance.
        runner.runLocal(getReaderAction('reject_decision'), () =>
          markResolved(decision.id),
        )
        return
      }
      const spec = specFor(decision, verdict)
      if (!spec) return
      void runner.run(spec)
    },
    [markResolved, runner, specFor],
  )

  return { resolve }
}
